import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { 
  Users, UserPlus, Mail, Shield, Eye, Settings, 
  Trash2, Crown, Check, X,
  Clock, AlertCircle, Copy, Send, Loader2
} from 'lucide-react';
import { useTeam, TeamRole, TeamMember } from '../../contexts/TeamContext';
import { sendInviteEmail, initGoogleAuth, requestAccessToken } from '../../lib/googleDrive';
import { useAuth } from '../../contexts/AuthContext';

interface TeamManagerProps {
  onClose?: () => void;
  isModal?: boolean;
}

const ROLE_LABELS: Record<TeamRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  viewer: 'Viewer',
};

// Role descriptions for tooltips
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ROLE_DESCRIPTIONS: Record<TeamRole, string> = {
  owner: 'Full access including billing & ownership transfer',
  admin: 'Full access including team management',
  manager: 'Can upload reports and manage settings',
  viewer: 'View-only access to dashboard',
};

const ROLE_COLORS: Record<TeamRole, string> = {
  owner: 'bg-amber-500/20 text-amber-400',
  admin: 'bg-purple-500/20 text-purple-400',
  manager: 'bg-blue-500/20 text-blue-400',
  viewer: 'bg-slate-500/20 text-slate-400',
};

const ROLE_ICONS: Record<TeamRole, React.ReactNode> = {
  owner: <Shield className="w-3 h-3" />,
  admin: <Crown className="w-3 h-3" />,
  manager: <Settings className="w-3 h-3" />,
  viewer: <Eye className="w-3 h-3" />,
};

export function TeamManager({ onClose, isModal = true }: TeamManagerProps) {
  const { 
    teamMembers, 
    pendingInvites, 
    maxMembers,
    inviteMember, 
    getInviteLink,
    // updateMemberRole, // Available for future use
    removeMember,
    cancelInvite,
    // resendInvite, // Not used - using handleSendEmail instead
    canManageTeam,
    canInviteMembers,
    isOwner,
    currentUserRole,
  } = useTeam();

  const { user } = useAuth();
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<TeamRole>('viewer');
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const handleInvite = async (sendEmail: boolean = false) => {
    if (!inviteEmail.trim()) return;
    
    const invite = inviteMember(inviteEmail.trim(), inviteRole);
    const link = getInviteLink(invite.id);
    
    if (sendEmail && link) {
      // Send email via Gmail
      setSendingEmail(invite.id);
      setEmailError(null);
      try {
        await initGoogleAuth();
        await requestAccessToken();
        await sendInviteEmail(
          inviteEmail.trim(),
          user?.name || user?.email || 'Your team admin',
          ROLE_LABELS[inviteRole],
          link
        );
        setEmailSent(invite.id);
        setTimeout(() => setEmailSent(null), 3000);
      } catch (err: any) {
        console.error('Failed to send email:', err);
        setEmailError(err.message || 'Failed to send email');
        // Still copy link as fallback
        if (link) {
          navigator.clipboard.writeText(link);
          setCopiedLink(invite.id);
          setTimeout(() => setCopiedLink(null), 3000);
        }
      } finally {
        setSendingEmail(null);
      }
    } else if (link) {
      navigator.clipboard.writeText(link);
      setCopiedLink(invite.id);
      setTimeout(() => setCopiedLink(null), 3000);
    }
    
    setInviteEmail('');
    setInviteRole('viewer');
    setInviteSuccess(true);
    setTimeout(() => {
      setInviteSuccess(false);
      setShowInviteForm(false);
    }, 2000);
  };
  
  const handleSendEmail = async (inviteId: string, email: string, role: TeamRole) => {
    const link = getInviteLink(inviteId);
    if (!link) return;
    
    setSendingEmail(inviteId);
    setEmailError(null);
    try {
      await initGoogleAuth();
      await requestAccessToken();
      await sendInviteEmail(
        email,
        user?.name || user?.email || 'Your team admin',
        ROLE_LABELS[role],
        link
      );
      setEmailSent(inviteId);
      setTimeout(() => setEmailSent(null), 3000);
    } catch (err: any) {
      console.error('Failed to send email:', err);
      setEmailError(err.message || 'Failed to send email');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleRemoveMember = (member: TeamMember) => {
    if (window.confirm(`Remove ${member.name || member.email} from the team?`)) {
      removeMember(member.id);
    }
  };
  
  const handleCopyLink = (inviteId: string) => {
    const link = getInviteLink(inviteId);
    if (link) {
      navigator.clipboard.writeText(link);
      setCopiedLink(inviteId);
      setTimeout(() => setCopiedLink(null), 3000);
    }
  };

  const totalMembers = teamMembers.length + pendingInvites.length;

  // Main content
  const content = (
    <div className="space-y-6">
      {/* Header with member count and invite button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-medium text-slate-200">Team Members</h3>
          <span className="text-sm text-slate-500">
            ({totalMembers}/{maxMembers})
          </span>
        </div>
        {canInviteMembers && !showInviteForm && (
          <button
            onClick={() => setShowInviteForm(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 
                     text-white rounded-lg transition-colors text-sm font-medium"
          >
            <UserPlus className="w-4 h-4" />
            Invite Member
          </button>
        )}
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <div className="bg-slate-800/50 rounded-lg p-4 border border-green-500/30">
          {inviteSuccess ? (
            <div className="flex items-center gap-2 text-green-400">
              <Check className="w-5 h-5" />
              <span>Invitation created! Link copied to clipboard.</span>
            </div>
          ) : (
            <>
              <h4 className="text-sm font-medium text-slate-200 mb-4">Invite Team Member</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@company.com"
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                             text-white placeholder-slate-500 focus:outline-none focus:ring-2 
                             focus:ring-green-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => {
                      e.stopPropagation();
                      setInviteRole(e.target.value as TeamRole);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg 
                             text-white focus:outline-none focus:ring-2 focus:ring-green-500 text-sm
                             cursor-pointer appearance-none"
                    style={{ WebkitAppearance: 'menulist' }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={() => handleInvite(true)}
                  disabled={!inviteEmail.trim() || sendingEmail !== null}
                  className="flex items-center gap-1 px-4 py-2 bg-green-600 hover:bg-green-700 
                           disabled:bg-slate-600 disabled:cursor-not-allowed
                           text-white rounded-lg transition-colors text-sm font-medium"
                >
                  {sendingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Email Invite
                </button>
                <button
                  onClick={() => handleInvite(false)}
                  disabled={!inviteEmail.trim()}
                  className="flex items-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 
                           disabled:bg-slate-800 disabled:cursor-not-allowed
                           text-slate-300 rounded-lg transition-colors text-sm"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </button>
                <button
                  onClick={() => setShowInviteForm(false)}
                  className="px-4 py-2 text-slate-400 hover:text-slate-200 text-sm"
                >
                  Cancel
                </button>
              </div>
              {emailError && (
                <p className="text-xs text-red-400 mt-2">{emailError}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Team Members List */}
      {teamMembers.length > 0 && (
        <div className="space-y-2">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                  <span className="text-lg font-medium text-slate-300">
                    {(member.name || member.email).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">{member.name || 'Unnamed'}</p>
                  <p className="text-xs text-slate-400">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${ROLE_COLORS[member.role]}`}>
                  {ROLE_ICONS[member.role]}
                  {ROLE_LABELS[member.role]}
                </span>
                {canManageTeam && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member)}
                    className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Invites
          </h4>
          {pendingInvites.map((invite) => (
            <div
              key={invite.id}
              className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-dashed border-slate-700"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-700/50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-slate-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-300">{invite.email}</p>
                  <p className="text-xs text-slate-500">
                    Invited as {ROLE_LABELS[invite.role]} • Expires in 7 days
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSendEmail(invite.id, invite.email, invite.role)}
                  disabled={sendingEmail === invite.id}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-green-400 
                           hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
                  title="Send email invite"
                >
                  {sendingEmail === invite.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : emailSent === invite.id ? (
                    <>
                      <Check className="w-3 h-3" />
                      Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-3 h-3" />
                      Send Email
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleCopyLink(invite.id)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-blue-400 
                           hover:bg-blue-500/10 rounded transition-colors"
                >
                  {copiedLink === invite.id ? (
                    <>
                      <Check className="w-3 h-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy Link
                    </>
                  )}
                </button>
                <button
                  onClick={() => cancelInvite(invite.id)}
                  className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                  title="Cancel invite"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {teamMembers.length === 0 && pendingInvites.length === 0 && (
        <div className="text-center py-8 text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No team members yet</p>
          <p className="text-sm text-slate-500 mt-1">Invite your colleagues to collaborate</p>
        </div>
      )}

      {/* Limit Warning */}
      {totalMembers >= maxMembers - 2 && totalMembers > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-yellow-400 font-medium">Approaching team limit</p>
              <p className="text-slate-400 mt-1">
                You have {maxMembers - totalMembers} invite{maxMembers - totalMembers !== 1 ? 's' : ''} remaining.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Inline mode - just return the content
  if (!isModal) {
    return content;
  }

  // Modal mode - wrap in portal
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Team Management</h1>
              <p className="text-white/70 text-sm">
                {isOwner ? 'Manage your organization' : `Role: ${ROLE_LABELS[currentUserRole]}`}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {content}
        </div>
      </div>
    </div>,
    document.body
  );
}
